import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import {
  fetchSettings,
  updateSettings,
  fetchCurrencies,
  fetchTimezones,
  selectSettings,
  selectCurrencies,
  selectTimezones,
  selectSettingsLoading,
  selectSettingsUpdating,
  selectSettingsError,
  selectSettingsInitialized,
  clearError
} from '../../store/slices/settingsSlice';
import { settingsApi, authApi } from '../../lib/api';
import { useSettings } from '../../contexts/SettingsContext';
import { 
  Building2, 
  DollarSign, 
  Mail, 
  Phone, 
  Globe, 
  Clock, 
  CalendarDays,
  MapPin,
  Save,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  X,
  User,
  Upload,
  Image as ImageIcon,
  Trash2,
  Lock,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'react-hot-toast';

interface FormData {
  hospitalName: string;
  currency: string;
  hospitalAddress: string;
  hospitalPhone: string;
  hospitalEmail: string;
  hospitalWebsite: string;
  hospitalLogo: string;
  timeZone: string;
  dateFormat: string;
  timeFormat: string;
}

interface UserPreferences {
  dateFormat: string;
  timeFormat: string;
  timeZone: string;
}

interface PasswordChangeData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const SettingsPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const { user } = useSelector((state: RootState) => state.auth);
  const settings = useSelector((state: RootState) => selectSettings(state));
  const currencies = useSelector((state: RootState) => selectCurrencies(state));
  const timezones = useSelector((state: RootState) => selectTimezones(state));
  const loading = useSelector((state: RootState) => selectSettingsLoading(state));
  const updating = useSelector((state: RootState) => selectSettingsUpdating(state));
  const error = useSelector((state: RootState) => selectSettingsError(state));
  const initialized = useSelector((state: RootState) => selectSettingsInitialized(state));

  const isAdmin = user?.role?.toUpperCase() === 'ADMIN';

  const [formData, setFormData] = useState<FormData>({
    hospitalName: '',
    currency: 'USD',
    hospitalAddress: '',
    hospitalPhone: '',
    hospitalEmail: '',
    hospitalWebsite: '',
    hospitalLogo: '',
    timeZone: 'UTC',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12'
  });

  const [userPreferences, setUserPreferences] = useState<UserPreferences>({
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12',
    timeZone: 'UTC'
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [preferencesLoading, setPreferencesLoading] = useState(false);
  const [preferencesUpdating, setPreferencesUpdating] = useState(false);
  const [preferencesError, setPreferencesError] = useState<string | null>(null);

  // Image upload state
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { refreshUserPreferences } = useSettings();

  const [appSettings, setAppSettings] = useState<any>(null);

  const [passwordData, setPasswordData] = useState<PasswordChangeData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  useEffect(() => {
    // Always load data on component mount
    console.log('SettingsPage: Loading initial data...', { initialized, currenciesCount: currencies.length, timezonesCount: timezones.length });
    dispatch(fetchSettings());
    dispatch(fetchCurrencies());
    dispatch(fetchTimezones());
    
    // Load user preferences if not admin
    if (!isAdmin) {
      loadUserPreferences();
    }
  }, [dispatch, isAdmin]);

  useEffect(() => {
    // Update form data when settings are loaded
    if (settings) {
      console.log('SettingsPage: Settings loaded, updating form', settings);
      setFormData({
        hospitalName: settings.hospitalName || '',
        currency: settings.currency || 'USD',
        hospitalAddress: settings.hospitalAddress || '',
        hospitalPhone: settings.hospitalPhone || '',
        hospitalEmail: settings.hospitalEmail || '',
        hospitalWebsite: settings.hospitalWebsite || '',
        hospitalLogo: settings.hospitalLogo || '',
        timeZone: settings.timeZone || 'UTC',
        dateFormat: settings.dateFormat || 'MM/DD/YYYY',
        timeFormat: settings.timeFormat || '12'
      });
    }
  }, [settings]);

  useEffect(() => {
    // Check if form has changes
    if (settings) {
      const hasFormChanges = (
        formData.hospitalName !== (settings.hospitalName || '') ||
        formData.currency !== (settings.currency || 'USD') ||
        formData.hospitalAddress !== (settings.hospitalAddress || '') ||
        formData.hospitalPhone !== (settings.hospitalPhone || '') ||
        formData.hospitalEmail !== (settings.hospitalEmail || '') ||
        formData.hospitalWebsite !== (settings.hospitalWebsite || '') ||
        formData.hospitalLogo !== (settings.hospitalLogo || '') ||
        formData.timeZone !== (settings.timeZone || 'UTC') ||
        formData.dateFormat !== (settings.dateFormat || 'MM/DD/YYYY') ||
        formData.timeFormat !== (settings.timeFormat || '12')
      );
      setHasChanges(hasFormChanges);
    }
  }, [formData, settings]);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSaveSuccess(false);
    if (error) {
      dispatch(clearError());
    }
  };

  const handleSave = async () => {
    try {
      if (isAdmin) {
        await dispatch(updateSettings({
          hospitalName: formData.hospitalName,
          currency: formData.currency as any,
          hospitalAddress: formData.hospitalAddress || undefined,
          hospitalPhone: formData.hospitalPhone || undefined,
          hospitalEmail: formData.hospitalEmail || undefined,
          hospitalWebsite: formData.hospitalWebsite || undefined,
          hospitalLogo: formData.hospitalLogo || undefined,
          timeZone: formData.timeZone,
          dateFormat: formData.dateFormat,
          timeFormat: formData.timeFormat
        })).unwrap();
      } else {
        await saveUserPreferences();
      }
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to update settings:', error);
    }
  };

  const saveUserPreferences = async () => {
    try {
      setPreferencesUpdating(true);
      setPreferencesError(null);
      const response = await settingsApi.updateUserPreferences({
        dateFormat: userPreferences.dateFormat,
        timeFormat: userPreferences.timeFormat,
        timeZone: userPreferences.timeZone
      });
      if (response.data.success) {
        setUserPreferences({
          dateFormat: response.data.data!.dateFormat,
          timeFormat: response.data.data!.timeFormat,
          timeZone: response.data.data!.timeZone
        });
        await refreshUserPreferences();
      }
    } catch (error: any) {
      console.error('Failed to save user preferences:', error);
      setPreferencesError(error.response?.data?.message || 'Failed to save preferences');
      throw error;
    } finally {
      setPreferencesUpdating(false);
    }
  };

  const handlePreferenceChange = (field: keyof UserPreferences, value: string) => {
    setUserPreferences(prev => ({ ...prev, [field]: value }));
    setSaveSuccess(false);
    setPreferencesError(null);
  };

  const handleRefresh = () => {
    dispatch(fetchSettings());
    dispatch(fetchCurrencies());
    dispatch(fetchTimezones());
    setSaveSuccess(false);
  };

  const clearLogo = () => {
    setFormData(prev => ({ ...prev, hospitalLogo: '' }));
    setLogoFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Image upload handlers
  const handleLogoUpload = (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setLogoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setLogoPreview(result);
      // Also update form data with base64 for immediate preview
      setFormData(prev => ({ ...prev, hospitalLogo: result }));
    };
    reader.readAsDataURL(file);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleLogoUpload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleLogoUpload(files[0]);
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  const loadUserPreferences = async () => {
    try {
      setPreferencesLoading(true);
      setPreferencesError(null);
      const response = await settingsApi.getUserPreferences();
      if (response.data.success && response.data.data) {
        setUserPreferences({
          dateFormat: response.data.data.dateFormat,
          timeFormat: response.data.data.timeFormat,
          timeZone: response.data.data.timeZone
        });
      }
    } catch (error: any) {
      console.error('Failed to load user preferences:', error);
      setPreferencesError(error.response?.data?.message || 'Failed to load preferences');
    } finally {
      setPreferencesLoading(false);
    }
  };

  const handlePasswordChange = (field: keyof PasswordChangeData, value: string) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    setPasswordError(null);
    setPasswordSuccess(false);
  };

  const handleChangePassword = async () => {
    try {
      // Validate password data
      if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
        setPasswordError('All fields are required');
        return;
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setPasswordError('New passwords do not match');
        return;
      }

      if (passwordData.newPassword.length < 6) {
        setPasswordError('New password must be at least 6 characters long');
        return;
      }

      if (passwordData.currentPassword === passwordData.newPassword) {
        setPasswordError('New password must be different from current password');
        return;
      }

      setChangingPassword(true);
      setPasswordError(null);

      await authApi.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
        confirmPassword: passwordData.confirmPassword
      });

      setPasswordSuccess(true);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      toast.success('Password changed successfully');
      
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (error: any) {
      console.error('Error changing password:', error);
      const errorMessage = error.response?.data?.message || 'Failed to change password';
      setPasswordError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setChangingPassword(false);
    }
  };

  const togglePasswordVisibility = (field: keyof typeof showPasswords) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  // Debug logging
  console.log('SettingsPage render:', { 
    currenciesCount: currencies.length, 
    timezonesCount: timezones.length, 
    loading, 
    initialized 
  });

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await settingsApi.getSettings();
        console.log('Settings API Response:', response);
        if (response.data.success) {
          setAppSettings(response.data.data);
        }
      } catch (error) {
        console.error('Settings API Error:', error);
      }
    };

    loadSettings();
  }, []);

  if ((isAdmin && loading && !initialized) || (!isAdmin && preferencesLoading)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading {isAdmin ? 'settings' : 'preferences'}...</p>
        </div>
      </div>
    );
  }

  // Show user preferences UI for non-admin users
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <User className="h-6 w-6 text-blue-600" />
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Personal Settings</h1>
                  <p className="text-gray-600 text-sm mt-1">
                    Configure your personal preferences and account settings
                  </p>
                </div>
              </div>
            </div>

            {/* Error Display */}
            {(preferencesError || passwordError) && (
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center space-x-2 text-red-600 bg-red-50 p-3 rounded-lg">
                  <AlertCircle className="h-5 w-5 flex-shrink-0" />
                  <span className="text-sm">{preferencesError || passwordError}</span>
                </div>
              </div>
            )}

            {/* Success Display */}
            {(saveSuccess || passwordSuccess) && (
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center space-x-2 text-green-600 bg-green-50 p-3 rounded-lg">
                  <CheckCircle className="h-5 w-5 flex-shrink-0" />
                  <span className="text-sm">
                    {passwordSuccess ? 'Password changed successfully!' : 'Preferences saved successfully!'}
                  </span>
                </div>
              </div>
            )}

            {/* Password Change Section */}
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <div className="flex items-center mb-4">
                <Lock className="h-5 w-5 text-blue-600 mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">Change Password</h2>
              </div>
              
              <div className="space-y-4">
                {/* Current Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.current ? 'text' : 'password'}
                      value={passwordData.currentPassword}
                      onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter your current password"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('current')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPasswords.current ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.new ? 'text' : 'password'}
                      value={passwordData.newPassword}
                      onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter new password (min. 6 characters)"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('new')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPasswords.new ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.confirm ? 'text' : 'password'}
                      value={passwordData.confirmPassword}
                      onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Confirm your new password"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('confirm')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPasswords.confirm ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Change Password Button */}
                <div className="pt-2">
                  <button
                    onClick={handleChangePassword}
                    disabled={changingPassword || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {changingPassword ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Lock className="h-4 w-4" />
                    )}
                    <span>{changingPassword ? 'Changing...' : 'Change Password'}</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Preferences Form */}
            <div className="p-4 sm:p-6 space-y-6">
              <div className="flex items-center mb-4">
                <Clock className="h-5 w-5 text-blue-600 mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">Display Preferences</h2>
              </div>

              {/* Date Format */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <CalendarDays className="inline h-4 w-4 mr-2" />
                  Date Format
                </label>
                <select
                  value={userPreferences.dateFormat}
                  onChange={(e) => handlePreferenceChange('dateFormat', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="MM/DD/YYYY">MM/DD/YYYY (US Format)</option>
                  <option value="DD/MM/YYYY">DD/MM/YYYY (European Format)</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD (ISO Format)</option>
                </select>
              </div>

              {/* Time Format */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="inline h-4 w-4 mr-2" />
                  Time Format
                </label>
                <select
                  value={userPreferences.timeFormat}
                  onChange={(e) => handlePreferenceChange('timeFormat', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="12">12-hour (AM/PM)</option>
                  <option value="24">24-hour</option>
                </select>
              </div>

              {/* Time Zone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Globe className="inline h-4 w-4 mr-2" />
                  Time Zone
                </label>
                <select
                  value={userPreferences.timeZone}
                  onChange={(e) => handlePreferenceChange('timeZone', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {timezones.map((timezone) => (
                    <option key={timezone.value} value={timezone.value}>
                      {timezone.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Save Preferences Button */}
              <div className="flex justify-end pt-4">
                <button
                  onClick={handleSave}
                  disabled={preferencesUpdating}
                  className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {preferencesUpdating ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  <span>{preferencesUpdating ? 'Saving...' : 'Save Preferences'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show admin hospital settings UI
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
              <div className="flex items-center space-x-3">
                <Building2 className="h-6 w-6 text-blue-600" />
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Hospital Settings</h1>
                  <p className="text-gray-600 text-sm mt-1">
                    Configure your hospital's branding, currency, and contact information
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-end">
                <button
                  onClick={handleRefresh}
                  className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors text-sm"
                  disabled={loading}
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">Refresh</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Success Alert */}
        {saveSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-green-800">Success</h3>
                <p className="text-sm text-green-700 mt-1">Settings updated successfully!</p>
              </div>
            </div>
          </div>
        )}

        {/* Settings Form */}
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
              <p className="text-gray-600 text-sm">Hospital name and branding settings</p>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <div>
                <label htmlFor="hospitalName" className="block text-sm font-medium text-gray-700 mb-2">
                  Hospital Name *
                </label>
                <input
                  type="text"
                  id="hospitalName"
                  value={formData.hospitalName}
                  onChange={(e) => handleInputChange('hospitalName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                  placeholder="Enter hospital name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <ImageIcon className="h-4 w-4 inline mr-1" />
                  Hospital Logo
                </label>
                
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />

                {/* Current logo display */}
                {formData.hospitalLogo ? (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                      <img
                        src={formData.hospitalLogo}
                        alt="Hospital Logo"
                        className="h-16 w-16 object-contain border border-gray-300 rounded-lg bg-white"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">Current Logo</p>
                        <p className="text-sm text-gray-500 truncate">
                          {logoFile ? logoFile.name : 'Uploaded image'}
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          onClick={triggerFileSelect}
                          className="px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                        >
                          Replace
                        </button>
                        <button
                          type="button"
                          onClick={clearLogo}
                          className="px-3 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Upload area */
                  <div
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={triggerFileSelect}
                    className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 hover:bg-blue-50 transition-colors cursor-pointer"
                  >
                    <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <div className="space-y-2">
                      <p className="text-lg font-medium text-gray-900">Upload Hospital Logo</p>
                      <p className="text-sm text-gray-500">
                        Drag and drop an image here, or click to select
                      </p>
                      <p className="text-xs text-gray-400">
                        PNG, JPG, GIF up to 5MB
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Financial Settings */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Financial Settings</h2>
              <p className="text-gray-600 text-sm">Currency and billing configuration</p>
            </div>
            <div className="p-4 sm:p-6">
              <div>
                <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-2">
                  <DollarSign className="h-4 w-4 inline mr-1" />
                  Currency *
                </label>
                <select
                  id="currency"
                  value={formData.currency}
                  onChange={(e) => handleInputChange('currency', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                >
                  {currencies.length > 0 ? (
                    currencies.map((currency) => (
                      <option key={currency.code} value={currency.code}>
                        {currency.code} - {currency.name} ({currency.symbol})
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="USD">USD - US Dollar ($)</option>
                      <option value="GBP">GBP - British Pound (£)</option>
                      <option value="EUR">EUR - Euro (€)</option>
                      <option value="PKR">PKR - Pakistani Rupee (₨)</option>
                      <option value="INR">INR - Indian Rupee (₹)</option>
                      <option value="CAD">CAD - Canadian Dollar (C$)</option>
                      <option value="AUD">AUD - Australian Dollar (A$)</option>
                      <option value="SAR">SAR - Saudi Riyal (﷼)</option>
                      <option value="AED">AED - UAE Dirham (د.إ)</option>
                    </>
                  )}
                </select>
                {currencies.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">Using fallback currency options</p>
                )}
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Contact Information</h2>
              <p className="text-gray-600 text-sm">Hospital contact details</p>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <div>
                <label htmlFor="hospitalAddress" className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="h-4 w-4 inline mr-1" />
                  Address
                </label>
                <textarea
                  id="hospitalAddress"
                  value={formData.hospitalAddress}
                  onChange={(e) => handleInputChange('hospitalAddress', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                  placeholder="Enter hospital address"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="hospitalPhone" className="block text-sm font-medium text-gray-700 mb-2">
                    <Phone className="h-4 w-4 inline mr-1" />
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="hospitalPhone"
                    value={formData.hospitalPhone}
                    onChange={(e) => handleInputChange('hospitalPhone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="hospitalEmail" className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail className="h-4 w-4 inline mr-1" />
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="hospitalEmail"
                    value={formData.hospitalEmail}
                    onChange={(e) => handleInputChange('hospitalEmail', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                    placeholder="contact@hospital.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="hospitalWebsite" className="block text-sm font-medium text-gray-700 mb-2">
                  <Globe className="h-4 w-4 inline mr-1" />
                  Website
                </label>
                <input
                  type="url"
                  id="hospitalWebsite"
                  value={formData.hospitalWebsite}
                  onChange={(e) => handleInputChange('hospitalWebsite', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                  placeholder="https://www.hospital.com"
                />
              </div>
            </div>
          </div>

          {/* Display Settings */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Display Settings</h2>
              <p className="text-gray-600 text-sm">Date, time, and localization preferences</p>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <div>
                <label htmlFor="timeZone" className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="h-4 w-4 inline mr-1" />
                  Time Zone
                </label>
                <select
                  id="timeZone"
                  value={formData.timeZone}
                  onChange={(e) => handleInputChange('timeZone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                >
                  {timezones.length > 0 ? (
                    timezones.map((timezone) => (
                      <option key={timezone.value} value={timezone.value}>
                        {timezone.label}
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="UTC">UTC (Coordinated Universal Time)</option>
                      <option value="America/New_York">Eastern Time (US & Canada)</option>
                      <option value="America/Chicago">Central Time (US & Canada)</option>
                      <option value="America/Denver">Mountain Time (US & Canada)</option>
                      <option value="America/Los_Angeles">Pacific Time (US & Canada)</option>
                      <option value="Europe/London">London</option>
                      <option value="Europe/Paris">Paris</option>
                      <option value="Europe/Berlin">Berlin</option>
                      <option value="Asia/Dubai">Dubai</option>
                      <option value="Asia/Karachi">Karachi</option>
                      <option value="Asia/Kolkata">Mumbai/New Delhi</option>
                      <option value="Asia/Shanghai">Beijing/Shanghai</option>
                      <option value="Asia/Tokyo">Tokyo</option>
                      <option value="Australia/Sydney">Sydney</option>
                    </>
                  )}
                </select>
                {timezones.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">Using fallback timezone options</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="dateFormat" className="block text-sm font-medium text-gray-700 mb-2">
                    <CalendarDays className="h-4 w-4 inline mr-1" />
                    Date Format
                  </label>
                  <select
                    id="dateFormat"
                    value={formData.dateFormat}
                    onChange={(e) => handleInputChange('dateFormat', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                  >
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="timeFormat" className="block text-sm font-medium text-gray-700 mb-2">
                    <Clock className="h-4 w-4 inline mr-1" />
                    Time Format
                  </label>
                  <select
                    id="timeFormat"
                    value={formData.timeFormat}
                    onChange={(e) => handleInputChange('timeFormat', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                  >
                    <option value="12">12-hour (AM/PM)</option>
                    <option value="24">24-hour</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex flex-col sm:flex-row justify-end mt-8 space-y-3 sm:space-y-0">
          <button
            onClick={handleSave}
            disabled={!hasChanges || updating || !formData.hospitalName.trim()}
            className={`w-full sm:w-auto flex items-center justify-center space-x-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              hasChanges && formData.hospitalName.trim() && !updating
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {updating ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>{updating ? 'Saving...' : 'Save Settings'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage; 